import React, { useState } from "react";
import { Card, Button } from "react-bootstrap";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, isAfter, startOfToday, endOfToday, subWeeks, subYears, isWithinInterval, isBefore } from "date-fns";

const presets = [
  "Live Now",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "Last 12 Months",
];

const DateRangeCalendar = ({
  currentMonth,
  setCurrentMonth,
  selectedPreset,
  setSelectedPreset,
}) => {
  const today = new Date();

  const [range, setRange] = useState({
    startDate: null,
    endDate: null,
  });

  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 }),
  });

  const handlePresetClick = (preset) => {
    let startDate, endDate;

    switch (preset) {
      case "Live Now":
        startDate = startOfToday();
        endDate = endOfToday();
        break;

      case "This Week":
        startDate = startOfWeek(today, { weekStartsOn: 1 });
        endDate = today;
        break;

      case "Last Week":
        startDate = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        endDate = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
        break;

      case "This Month":
        startDate = startOfMonth(today);
        endDate = today;
        break;

      case "Last Month":
        startDate = startOfMonth(subMonths(today, 1));
        endDate = endOfMonth(subMonths(today, 1));
        break;

      case "Last 12 Months":
        startDate = startOfMonth(subYears(today, 1));
        endDate = today;
        break;

      default:
        return;
    }

    setRange({ startDate, endDate });
    setCurrentMonth(startDate);
    setSelectedPreset(preset);
  };

  const handleDateClick = (day) => {
    if (isAfter(day, today)) return;

    if (!range.startDate || range.endDate) {
      setRange({ startDate: day, endDate: null });
      setSelectedPreset("Custom Range");
      return;
    }

    if (isBefore(day, range.startDate)) {
      setRange({ startDate: day, endDate: range.startDate });
    } else {
      setRange({ startDate: range.startDate, endDate: day });
    }
  };

  return (
    <Card className="p-3 shadow-sm bg-light" style={{ width: 420 }}>
      <div className="d-flex">
        <div className="border-end pe-3 me-3" style={{ width: 140 }}>
          {presets.map((item) => (
            <div
              key={item}
              className={`calendar-preset ${
                selectedPreset === item ? "active" : ""
              }`}
              onClick={() => handlePresetClick(item)}
            >
              {item}
            </div>
          ))}
        </div>

        <div className="flex-grow-1">
          <div className="hstack justify-content-between mb-2">
            <Button
              size="sm"
              variant="outline-secondary"
              style={{ flex: 0 }}
              className="px-3"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              ‹
            </Button>

            <strong>{format(currentMonth, "MMM yyyy")}</strong>

            <Button
              size="sm"
              variant="outline-secondary"
              style={{ flex: 0 }}
              className="px-3"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              ›
            </Button>
          </div>
          <div className="d-grid calendar-grid mb-1">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
              <small key={d} className="text-center text-muted">
                {d}
              </small>
            ))}
          </div>

          <div className="d-grid calendar-grid">
            {days.map((day) => {
              const isFuture = isAfter(day, today);

              const isInRange =
                range.startDate &&
                range.endDate &&
                isWithinInterval(day, {
                  start: range.startDate,
                  end: range.endDate,
                });

              const isStart =
                range.startDate &&
                format(day, "yyyy-MM-dd") ===
                  format(range.startDate, "yyyy-MM-dd");

              const isEnd =
                range.endDate &&
                format(day, "yyyy-MM-dd") ===
                  format(range.endDate, "yyyy-MM-dd");

              return (
                <button
                  key={day}
                  disabled={isFuture}
                  onClick={() => handleDateClick(day)}
                  className={`calendar-day
                    ${!isSameMonth(day, currentMonth) ? "muted" : ""}
                    ${isToday(day) ? "today" : ""}
                    ${isInRange || isStart || isEnd ? "active" : ""}
                    ${isFuture ? "disabled" : ""}
                  `}
                >
                  {format(day, "d")}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default DateRangeCalendar;
